import { Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { ProdutoDesenho } from '../../interfaces/produto/produto-desenho.interface';
import { posicionarPopover } from '../../utils/popover-posicao';

/**
 * Coluna "Desenho" de produto nas listagens: todos os desenhos ativos separados por "; " e,
 * com o mouse em cima (ou foco pelo teclado), um cartão com a lista organizada — número,
 * padrão, versão e observação.
 *
 * <p>Sem desenhos na lista, mostra o campo antigo `produto.numeroDesenho` (produtos que só
 * têm o legado) e, sem nada, o texto `vazio`. Só leitura: não seleciona nem altera nada.</p>
 *
 * <p>O cartão é movido para o `<body>` ao abrir e posicionado com `position: fixed` (mesmo
 * helper dos popovers de estoque do pedido): dentro da tabela, o `overflow` do
 * `.table-responsive` o cortaria, e o `transform` de hover do `.card-industrial` o
 * deslocaria.</p>
 */
@Component({
  selector: 'app-desenhos-produto',
  templateUrl: './desenhos-produto.component.html',
  styleUrl: './desenhos-produto.component.scss'
})
export class DesenhosProdutoComponent implements OnDestroy {
  /** Desenhos ativos do produto (`desenhosAtivos` da API), padrão primeiro. */
  @Input() desenhos: ProdutoDesenho[] | null | undefined;

  /** Campo antigo `produto.numeroDesenho`, usado só quando a lista está vazia. */
  @Input() desenhoLegado: string | null | undefined;

  /** Texto quando o produto não tem desenho nenhum. */
  @Input() vazio = '—';

  @ViewChild('ancora', { static: true }) ancora!: ElementRef<HTMLElement>;

  @ViewChild('cartao') set cartaoRef(ref: ElementRef<HTMLElement> | undefined) {
    if (!ref) return;
    this.cartaoEl = ref.nativeElement;
    // Sai da célula da tabela antes de medir: ver o comentário da classe.
    document.body.appendChild(ref.nativeElement);
    posicionarPopover(ref.nativeElement, this.ancora.nativeElement);
  }

  public aberto = false;

  private timerAbrir: ReturnType<typeof setTimeout> | null = null;
  private timerFechar: ReturnType<typeof setTimeout> | null = null;
  private cartaoEl: HTMLElement | null = null;

  private readonly aoRolar = (evento: Event) => {
    // Rolar a própria lista do cartão não fecha; rolar a página/tabela fecha (a âncora sai
    // do lugar e o cartão ficaria flutuando no vazio).
    const alvo = evento.target as Node | null;
    if (alvo && this.cartaoEl?.contains(alvo)) return;
    this.zone.run(() => this.fechar());
  };
  private readonly aoTeclar = (evento: KeyboardEvent) => {
    if (evento.key === 'Escape') this.zone.run(() => this.fechar());
  };
  private readonly aoRedimensionar = () => this.zone.run(() => this.fechar());

  constructor(private zone: NgZone, private host: ElementRef<HTMLElement>) {}

  get lista(): ProdutoDesenho[] {
    return (this.desenhos ?? []).filter(d => !!d?.numeroDesenho?.trim() && d.ativo !== false);
  }

  get texto(): string {
    const lista = this.lista;
    if (lista.length > 0) return lista.map(d => d.numeroDesenho.trim()).join('; ');
    return this.desenhoLegado?.trim() || this.vazio;
  }

  get temTooltip(): boolean {
    return this.lista.length > 0;
  }

  agendarAbrir(): void {
    if (!this.temTooltip) return;
    this.cancelarFechar();
    if (this.aberto || this.timerAbrir) return;
    this.timerAbrir = setTimeout(() => {
      this.timerAbrir = null;
      this.abrir();
    }, 150);
  }

  agendarFechar(): void {
    if (this.timerAbrir) {
      clearTimeout(this.timerAbrir);
      this.timerAbrir = null;
    }
    if (!this.aberto || this.timerFechar) return;
    // Folga para o mouse atravessar da âncora até o cartão sem fechar.
    this.timerFechar = setTimeout(() => {
      this.timerFechar = null;
      this.fechar();
    }, 200);
  }

  cancelarFechar(): void {
    if (this.timerFechar) {
      clearTimeout(this.timerFechar);
      this.timerFechar = null;
    }
  }

  trackByDesenho(_i: number, d: ProdutoDesenho): number | string {
    return d.id ?? d.numeroDesenho;
  }

  private abrir(): void {
    // A âncora pode ter saído da tela (troca de página, linha removida) durante o atraso.
    if (!this.host.nativeElement.isConnected) return;
    this.aberto = true;
    // Fora da zona: scroll dispara dezenas de eventos por segundo e não deve rodar o
    // change detection da tela a cada um. Os handlers entram na zona só para fechar.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.aoRolar, true);
      window.addEventListener('resize', this.aoRedimensionar);
      document.addEventListener('keydown', this.aoTeclar);
    });
  }

  private fechar(): void {
    this.cancelarFechar();
    if (this.timerAbrir) {
      clearTimeout(this.timerAbrir);
      this.timerAbrir = null;
    }
    if (!this.aberto) return;
    this.aberto = false;
    this.cartaoEl = null;
    window.removeEventListener('scroll', this.aoRolar, true);
    window.removeEventListener('resize', this.aoRedimensionar);
    document.removeEventListener('keydown', this.aoTeclar);
  }

  ngOnDestroy(): void {
    this.fechar();
  }
}
